import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { uniqueId } from 'lodash';
import { useSelector } from 'react-redux';
import { getHeader } from '../../redux/App/App.slice';
import { useTranslation } from 'react-i18next';
import HeaderUserBarComponents from './HeaderUserBar.components';
import { HeaderActionTypes, HeaderTabType } from '../../types/header.types';
import HeaderTabComponent from './HeaderTab.component';
import { HeaderActionType } from '../../redux/App/App.type';
import IconButtonComponent from '../IconButton.component';
import { StyledBaseIcon } from '../Icons/Base.icon';
import { HEADER_ACTIONS_ICONS } from '../Icons/HeaderAction.icons';
import SelectComponent from '../Input/Select.component';
import { isDevelop } from '../../utils/app';
import {
  CentrifugoEventType,
  useCentrifugoManager,
} from '../../hooks/CentrifugoManager.hook';
import {
  setCandidateMetaAction,
  setIsEditCandidateAction,
} from '../../redux/Candidate/Candidate.slice';
import { useAppDispatch } from '../../hooks/useAppDispatcher.hook';
import ChevronLeftIcon from '../Icons/ChevronLeft.icon';
import { checkUserRequiredPermissions } from '../../utils/getUserRolesByToken';
import { UserPermissions } from '../../types/userPermissions.types';
import useAuthHook from '../../hooks/useAuth.hook';
import { useBackButtonClick } from '../../hooks/useBackButtonClick.hook';

const StyledDebugLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 4px;
  background: repeating-linear-gradient(
    -45deg,
    black 0 10px,
    #ffd500 10px 20px
  );
`;
const StyledHeaderTabs = styled.div`
  display: flex;
  gap: 10px;
`;
const StyledHeaderActions = styled.div`
  display: flex;
  gap: 15px;

  ${StyledBaseIcon} {
    color: ${({ theme }) => theme.colors.LABEL};
  }
`;
const StyledHeaderTitle = styled.div`
  font-weight: 600;
  font-size: 20px;
  color: ${({ theme }) => theme.colors.TITLE};
  white-space: nowrap;
  max-width: 600px;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const StyledHeaderSubTitle = styled.div`
  font-weight: 500;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.LABEL};
`;
const StyledHeaderTitleContent = styled.div`
  display: flex;
  flex-direction: column;
`;
const StyledHeaderTitleWrapper = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
`;
const StyledHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 20px;
  background: ${({ theme }) => theme.colors.CARD};
  border-bottom: 1px solid ${({ theme }) => theme.colors.BORDER};
  padding: 0 20px;
  position: relative;

  // Hack for detect Safari 13+ macOS
  @media not all and (min-resolution: 0.001dpcm) {
    @supports (-webkit-appearance: none) and (display: flow-root) {
      border-top: 1px solid ${({ theme }) => theme.colors.BLUE.B500};

      :before {
        content: '';
        position: absolute;
        left: -1px;
        top: -1px;
        width: 16px;
        height: 16px;
        background: ${({ theme }) => theme.colors.BLUE.B400};
      }

      :after {
        content: '';
        position: absolute;
        left: -1px;
        top: -1px;
        width: 16px;
        height: 16px;
        border-radius: 8px 0 0;
        border-top: 1px solid ${({ theme }) => theme.colors.BLUE.B500};
        border-left: 1px solid ${({ theme }) => theme.colors.BLUE.B500};
        background: ${({ theme }) => theme.colors.CARD};
      }
    }
  }
`;

const HeaderComponent = () => {
  const dispatch = useAppDispatch();
  const headerState = useSelector(getHeader);
  const { t } = useTranslation();
  const { subscribe, unsubscribe } = useCentrifugoManager();
  const { user } = useAuthHook();
  const onBackButtonClick = useBackButtonClick();

  useEffect(() => {
    subscribe(CentrifugoEventType.META_EVENT, (notification: any) => {
      dispatch(
        setCandidateMetaAction({
          BASE_COUNTER: notification.BASE_COUNTER,
          COMMUNICATION_COUNTER: notification.COMMUNICATION_COUNTER,
          REQUESTS_COUNTER: notification.REQUESTS_COUNTER,
          WAIT_COUNTER: notification.WAIT_COUNTER,
          WORK_COUNTER: notification.WORK_COUNTER,
        }),
      );
    });

    return () => {
      unsubscribe(CentrifugoEventType.META_EVENT);
    };
  }, []);

  useEffect(() => {
    let title = 'Hunter CRM';
    if (headerState.title) {
      title = `${t(headerState.title)} | ${title}`;
    }
    document.title = title;
  }, [headerState.title]);

  const renderHeaderActions = useMemo(() => {
    if (headerState.actions.length === 0) return undefined;

    const renderAction = ({
      type,
      icon,
      action,
      options,
    }: HeaderActionType) => {
      if (type === HeaderActionTypes.ICON_BUTTON) {
        return (
          checkUserRequiredPermissions(user.permissions, [
            icon === "EDIT"
              ? UserPermissions.CAN_EDIT_CANDIDATE_RECORD
              : UserPermissions.CAN_DELETE_CANDIDATE_RECORD,
          ]) &&
          <IconButtonComponent
            key={uniqueId('header-action-')}
            onClick={() => {
              if (icon === 'EDIT') {
                dispatch(setIsEditCandidateAction(true));
              }
              action();
            }}
          >
            {icon && HEADER_ACTIONS_ICONS[icon]}
          </IconButtonComponent>
        );
      }

      if (type === HeaderActionTypes.SELECT) {
        return (
          <SelectComponent
            key={uniqueId('header-action-')}
            useMenuPortalTarget
            {...options}
          />
        );
      }

      return undefined;
    };

    return headerState.actions.map(renderAction);
  }, [headerState, headerState.actions]);

  return (
    <React.Fragment>
      <StyledHeader>
        <StyledHeaderTitleWrapper>
          {onBackButtonClick && (
            <IconButtonComponent onClick={onBackButtonClick}>
              <ChevronLeftIcon />
            </IconButtonComponent>
          )}
          <StyledHeaderTitleContent>
            {headerState.subTitle && (
              <StyledHeaderSubTitle>
                {t(headerState.subTitle)}
              </StyledHeaderSubTitle>
            )}
            <StyledHeaderTitle>{t(headerState.title)}</StyledHeaderTitle>
          </StyledHeaderTitleContent>
          {headerState.actions.length > 0 && (
            <StyledHeaderActions>{renderHeaderActions}</StyledHeaderActions>
          )}
        </StyledHeaderTitleWrapper>
        <StyledHeaderTabs>
          {headerState.tabs.map((tab: HeaderTabType) => (
            <HeaderTabComponent key={uniqueId('header-tab-')} {...tab}>
              {t(tab.text)}
            </HeaderTabComponent>
          ))}
        </StyledHeaderTabs>
        <HeaderUserBarComponents />
      </StyledHeader>
      {isDevelop() && <StyledDebugLine />}
    </React.Fragment>
  );
};

export default HeaderComponent;
